/**
 * Recipe Loading Module
 *
 * Imports all recipe JSON files, validates them at load time (fail-fast),
 * converts each to TemplateDefinition via the adapter, and exports the
 * resulting templates along with recipe-specific extras.
 */

import { validateRecipe, type Recipe } from '@/types/recipe';
import type { TemplateDefinition } from '@/types/template';
import {
  recipeToTemplateDefinition,
  extractRecipeExtras,
  type RecipeExtras,
} from './recipeAdapter';

// Static recipe JSON imports
import toolCallUriHttpRaw from './tool-call-uri-http.recipe.json';
import toolExecuteQuerySqlRaw from './tool-execute-query-sql.recipe.json';
import toolGetWeatherMsnRaw from './tool-get-weather-msn.recipe.json';
import toolGetRowsDataverseRaw from './tool-get-rows-dataverse.recipe.json';
import receiveRequestSendResponseRaw from './receive-request-send-response.recipe.json';
import toolSendEmailOutlookRaw from './tool-send-email-outlook.recipe.json';
import orderValidationRaw from './order-validation.recipe.json';
import supportEmailTriageRaw from './support-email-triage.recipe.json';
import toolSentimentAnalysisRaw from './tool-sentiment-analysis.recipe.json';
import toolExpenseReportRaw from './tool-expense-report.recipe.json';

// =============================================================================
// VALIDATION & LOADING
// =============================================================================

function loadRecipe(raw: unknown, filename: string): Recipe {
  const result = validateRecipe(raw);
  if (!result.success) {
    throw new Error(`Invalid recipe "${filename}":\n  ${result.errors.join('\n  ')}`);
  }
  return result.data;
}

const ALL_RECIPES: Recipe[] = [
  loadRecipe(receiveRequestSendResponseRaw, 'receive-request-send-response.recipe.json'),
  loadRecipe(toolCallUriHttpRaw, 'tool-call-uri-http.recipe.json'),  
  loadRecipe(toolGetWeatherMsnRaw, 'tool-get-weather-msn.recipe.json'),
  loadRecipe(toolSendEmailOutlookRaw, 'tool-send-email-outlook.recipe.json'),
  loadRecipe(toolExecuteQuerySqlRaw, 'tool-execute-query-sql.recipe.json'),
  loadRecipe(toolGetRowsDataverseRaw, 'tool-get-rows-dataverse.recipe.json'),
  loadRecipe(orderValidationRaw, 'order-validation.recipe.json'),
  loadRecipe(supportEmailTriageRaw, 'support-email-triage.recipe.json'),
  loadRecipe(toolSentimentAnalysisRaw, 'tool-sentiment-analysis.recipe.json'),
  loadRecipe(toolExpenseReportRaw, 'tool-expense-report.recipe.json'),
];

// =============================================================================
// EXPORTS
// =============================================================================

/** Built-in templates converted from recipes — drop-in replacement for BUILTIN_TEMPLATES. */
export const RECIPE_TEMPLATES: TemplateDefinition[] = ALL_RECIPES.map(recipeToTemplateDefinition);

/** Pre-computed extras indexed by recipe metadata.id for O(1) lookup. */
const extrasMap = new Map<string, RecipeExtras>(
  ALL_RECIPES.map((r) => [r.metadata.id, extractRecipeExtras(r)])
);

/** Pre-computed recipes indexed by metadata.id for O(1) lookup. */
const recipeMap = new Map<string, Recipe>(
  ALL_RECIPES.map((r) => [r.metadata.id, r])
);

/** Get recipe-specific extras (trigger sample data, mocks) by template ID. */
export function getRecipeExtras(templateId: string): RecipeExtras | undefined {
  return extrasMap.get(templateId);
}

/** Get the raw validated Recipe object by template ID. */
export function getRecipeById(templateId: string): Recipe | undefined {
  return recipeMap.get(templateId);
}
